// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Crew Activity Generator
 * Generates daily activities based on relationships and location
 */

import { ACTIVITY_TEMPLATES, ACTIVITY_TYPES, LOCATION_MODIFIERS } from '../../data/crewActivities.js';
import EventBus, { EVENT_TYPES } from '../system/EventBus.js';
import CrewManager from './CrewManager.js';

export class CrewActivityGenerator {
  constructor() {
    this.lastActivityTime = Date.now();
    this.activityHistory = [];
  }

  /**
   * Generate activities for the crew based on current conditions
   */
  generateDailyActivities(crew, relationships, currentLocation, shipModules = []) {
    const activities = [];
    const availableCrew = crew.filter(member => !member.injured && member.morale > 10);
    
    if (availableCrew.length === 0) return activities;

    // Generate individual activities
    availableCrew.forEach(crewMember => {
      const activity = this.generateIndividualActivity(crewMember, currentLocation, shipModules);
      if (activity) activities.push(activity);
    });

    // Generate social activities
    if (availableCrew.length >= 2) {
      const socialActivity = this.generateSocialActivity(availableCrew, relationships, currentLocation);
      if (socialActivity) activities.push(socialActivity);
    }

    // Generate group activities
    if (availableCrew.length >= 3) {
      const groupActivity = this.generateGroupActivity(availableCrew, currentLocation);
      if (groupActivity) activities.push(groupActivity);
    }

    return activities;
  }

  /**
   * Generate individual activity for a crew member
   */
  generateIndividualActivity(crewMember, location, modules) {
    const workActivities = ACTIVITY_TEMPLATES[ACTIVITY_TYPES.WORK];
    const recreationActivities = ACTIVITY_TEMPLATES[ACTIVITY_TYPES.RECREATION];
    
    const availableActivities = [
      ...workActivities.filter(a => this.meetsRequirements(a, { crewMember, location, modules })),
      ...recreationActivities.filter(a => this.meetsRequirements(a, { crewMember, location, modules }))
    ];

    if (availableActivities.length === 0) return null;

    const activity = availableActivities[Math.floor(Math.random() * availableActivities.length)];
    return this.createActivity(activity, [crewMember], location);
  }

  /**
   * Generate social activity between crew members
   */
  generateSocialActivity(crew, relationships, location) {
    const socialActivities = ACTIVITY_TEMPLATES[ACTIVITY_TYPES.SOCIAL];
    const conflictActivities = ACTIVITY_TEMPLATES[ACTIVITY_TYPES.CONFLICT];
    
    // Pick two random crew members
    const crew1 = crew[Math.floor(Math.random() * crew.length)];
    const crew2 = crew.filter(c => c.id !== crew1.id)[Math.floor(Math.random() * (crew.length - 1))];
    
    const relationship = relationships[`${crew1.id}-${crew2.id}`] || 0;
    
    // Choose activity type based on relationship
    let availableActivities;
    if (relationship < -10) {
      availableActivities = conflictActivities;
    } else if (relationship > 20) {
      availableActivities = socialActivities;
    } else {
      availableActivities = [...socialActivities, ...conflictActivities];
    }

    availableActivities = availableActivities.filter(a => 
      this.meetsRequirements(a, { crew: [crew1, crew2], location, relationship })
    );

    if (availableActivities.length === 0) return null;

    const activity = availableActivities[Math.floor(Math.random() * availableActivities.length)];
    return this.createActivity(activity, [crew1, crew2], location);
  }

  /**
   * Generate group activity
   */
  generateGroupActivity(crew, location) {
    const groupActivities = ACTIVITY_TEMPLATES[ACTIVITY_TYPES.GROUP];
    
    const availableActivities = groupActivities.filter(a => 
      this.meetsRequirements(a, { crew, location })
    );

    if (availableActivities.length === 0) return null;

    const activity = availableActivities[Math.floor(Math.random() * availableActivities.length)];
    return this.createActivity(activity, crew, location);
  }

  /**
   * Check if activity requirements are met
   */
  meetsRequirements(activity, context) {
    const { requirements } = activity;
    if (!requirements) return true;

    // Location check
    if (requirements.location && !requirements.location.includes(context.location)) {
      return false;
    }

    // Role check
    if (requirements.roles && context.crewMember) {
      if (!requirements.roles.includes(context.crewMember.role)) {
        return false;
      }
    }

    // Module check
    if (requirements.modules && context.modules) {
      if (!requirements.modules.some(mod => context.modules.includes(mod))) {
        return false;
      }
    }

    // Relationship checks
    if (requirements.minRelationship && context.relationship < requirements.minRelationship) {
      return false;
    }
    if (requirements.maxRelationship && context.relationship > requirements.maxRelationship) {
      return false;
    }

    // Crew count check
    if (requirements.minCrew && context.crew && context.crew.length < requirements.minCrew) {
      return false;
    }

    return true;
  }

  /**
   * Create activity instance with formatted description
   */
  createActivity(template, participants, location) {
    const locationModifier = LOCATION_MODIFIERS[location] || { moraleMultiplier: 1.0 };
    
    let description = template.description;
    participants.forEach((crew, index) => {
      description = description.replace(`{crew${index + 1}}`, crew.name);
    });

    return {
      id: `${template.id}_${Date.now()}`,
      templateId: template.id,
      name: template.name,
      description,
      participants: participants.map(p => p.id),
      duration: template.duration,
      effects: {
        relationshipChange: template.relationshipChange || 0,
        moraleChange: Math.round((template.moraleChange || 0) * locationModifier.moraleMultiplier),
        xpChange: template.xpChange || 0
      },
      location,
      timestamp: Date.now(),
      isGroup: template.isGroup || false
    };
  }

  /**
   * Apply activity effects to crew and relationships
   */
  applyActivityEffects(activity, crewManager, relationshipManager) {
    const { participants, effects, isGroup } = activity;
    
    // Apply individual effects
    participants.forEach(crewId => {
      const crew = crewManager.getCrewMember(crewId);
      if (!crew) return;

      if (effects.xpChange) {
        crewManager.awardExperience(crewId, effects.xpChange);
      }
    });

    // Apply morale effects to overall crew
    if (effects.moraleChange) {
      crewManager.adjustMorale(effects.moraleChange);
    }

    // Apply relationship effects
    if (effects.relationshipChange && participants.length >= 2 && !isGroup) {
      relationshipManager.adjustRelationship(
        participants[0], 
        participants[1], 
        effects.relationshipChange
      );
    } else if (effects.relationshipChange && isGroup) {
      // Apply to all pairs in group
      for (let i = 0; i < participants.length; i++) {
        for (let j = i + 1; j < participants.length; j++) {
          relationshipManager.adjustRelationship(
            participants[i], 
            participants[j], 
            effects.relationshipChange
          );
        }
      }
    }
  }

  /**
   * Process daily activities (called by GameTimeManager on day change)
   */
  processDailyActivities() {
    try {
      const crew = CrewManager.getAllCrew();
      if (crew.length === 0) return [];

      // Simple relationship map (can be enhanced with RelationshipManager later)
      const relationships = {};
      
      // Generate activities for the day
      const activities = this.generateDailyActivities(crew, relationships, 'space', []);
      
      // Apply effects and emit events
      activities.forEach(activity => {
        this.applyActivityEffects(activity, CrewManager, { 
          adjustRelationship: () => {} // Placeholder for relationship manager
        });
        
        // Emit crew activity event for UI
        EventBus.emit(EVENT_TYPES.GAME_EVENT, {
          type: 'crew_activity',
          message: activity.description,
          timestamp: Date.now(),
          activity: activity
        });
      });

      return activities;
    } catch (error) {
      console.error('[CrewActivityGenerator] Error processing daily activities:', error);
      return [];
    }
  }

  /**
   * Get recent activity history
   */
  getRecentActivities(hours = 24) {
    const cutoff = Date.now() - (hours * 60 * 60 * 1000);
    return this.activityHistory.filter(a => a.timestamp > cutoff);
  }

  /**
   * Process periodic crew activities (called by game time system)
   */
  processPeriodicActivities(crewManager, relationshipManager, currentLocation, shipModules = []) {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;
    const activityInterval = 30 * 60 * 1000; // 30 minutes in milliseconds
    
    // Check if enough time has passed for new activities
    if (timeSinceLastActivity < activityInterval) {
      return [];
    }

    const crew = crewManager.getAllCrew();
    const relationships = {};
    
    // Build relationships map for activity generator
    crew.forEach(member1 => {
      crew.forEach(member2 => {
        if (member1.id !== member2.id) {
          const rel = relationshipManager.getRelationship(member1.id, member2.id);
          relationships[`${member1.id}-${member2.id}`] = rel.score - 50; // Convert to -50 to +50 range
        }
      });
    });

    // Generate activities
    const activities = this.generateDailyActivities(crew, relationships, currentLocation, shipModules);
    
    // Apply effects for generated activities
    activities.forEach(activity => {
      this.applyActivityEffects(activity, crewManager, relationshipManager);
    });

    this.lastActivityTime = now;
    return activities;
  }
}

// Export singleton instance
const crewActivityGenerator = new CrewActivityGenerator();
export default crewActivityGenerator;